import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface AdminEventAuthor {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface AdminEventClub {
  id: string;
  name: string;
  imageUrl: string | null;
}

export interface AdminEventListItem {
  id: string;
  name: string;
  imageUrl: string | null;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  timezone: string | null;
  location: {
    address: string;
    city: string;
    country: string;
    longitude?: number;
    latitude?: number;
  };
  capacity: { min: number; max: number };
  status: string;
  onlyForClubMembers: boolean;
  clubId: string | null;
  freeSlotsLeft: number | null;
  firstThreeParticipants:
    | Array<{
        id: string;
        firstName: string;
        lastName: string;
        avatarUrl?: string;
      }>
    | null;
  community: { link: string; name: string } | null;
  author: AdminEventAuthor | null;
  club: AdminEventClub | null;
}

export interface AdminEventListResponse {
  items: AdminEventListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminEventParticipant {
  applicationId: string;
  userId: string;
  isFree: boolean;
  joinedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl: string | null;
  } | null;
}

export interface AdminEventReport {
  id: string;
  reason: string;
  details: string | null;
  status: string;
  reporter: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  } | null;
  moderator: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  moderatorNotes: string | null;
  createdAt: string;
}

export interface AdminEventDetail extends AdminEventListItem {
  description: string;
  category: string | null;
  maxAge: number;
  minAge: number;
  fee: number | null;
  currency: string | null;
  freeSlotsTotal: number | null;
  source: string;
  externalId: string | null;
  ticketUrl: string | null;
  inProgressJoinEnabled: boolean;
  coCreators: Array<{
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  }>;
  tags: Array<{ id: string; name: string }>;
  activities: Array<{ id: string; name: string }>;
}

export type EventStatusValue =
  | 'upcoming'
  | 'in_progress'
  | 'cancelled'
  | 'finished';

export interface ListEventsArgs {
  search?: string;
  status?: EventStatusValue;
  page?: number;
  limit?: number;
}

export const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery,
  tagTypes: ['Event', 'EventList'],
  endpoints: (builder) => ({
    listEvents: builder.query<AdminEventListResponse, ListEventsArgs>({
      query: ({ search, status, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (search && search.trim()) params.set('search', search.trim());
        if (status) params.set('status', status);
        params.set('page', String(page));
        params.set('limit', String(limit));
        return { url: `/admin/events?${params.toString()}` };
      },
      providesTags: ['EventList'],
    }),
    getEvent: builder.query<AdminEventDetail, string>({
      query: (id) => ({ url: `/admin/events/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Event', id }],
    }),
    getEventReports: builder.query<AdminEventReport[], string>({
      query: (id) => ({ url: `/admin/events/${id}/reports` }),
      providesTags: (_result, _error, id) => [
        { type: 'Event', id: `${id}-reports` },
      ],
    }),
    getEventParticipants: builder.query<AdminEventParticipant[], string>({
      query: (id) => ({ url: `/admin/events/${id}/participants` }),
      providesTags: (_result, _error, id) => [
        { type: 'Event', id: `${id}-participants` },
      ],
    }),
    addEventParticipant: builder.mutation<
      { success: boolean },
      { eventId: string; userId: string }
    >({
      query: ({ eventId, userId }) => ({
        url: `/admin/events/${eventId}/participants`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: 'Event', id: eventId },
        { type: 'Event', id: `${eventId}-participants` },
        'EventList',
      ],
    }),
    removeEventParticipant: builder.mutation<
      { success: boolean },
      { eventId: string; userId: string }
    >({
      query: ({ eventId, userId }) => ({
        url: `/admin/events/${eventId}/participants/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: 'Event', id: eventId },
        { type: 'Event', id: `${eventId}-participants` },
        'EventList',
      ],
    }),
    updateEventStatus: builder.mutation<
      AdminEventDetail,
      { id: string; status: EventStatusValue }
    >({
      query: ({ id, status }) => ({
        url: `/admin/events/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Event', id },
        'EventList',
      ],
    }),
    deleteEvent: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/admin/events/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EventList'],
    }),
  }),
});

export const {
  useListEventsQuery,
  useGetEventQuery,
  useGetEventReportsQuery,
  useGetEventParticipantsQuery,
  useUpdateEventStatusMutation,
  useDeleteEventMutation,
  useAddEventParticipantMutation,
  useRemoveEventParticipantMutation,
} = eventsApi;
